/* eslint-disable react/prop-types */
import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Check, Headphones, Image as ImageIcon, CircleHelp, Layers, Bell, BadgePercent, ChevronRight } from "lucide-react";
import { apiGet, apiPost } from "../lib/api";

const DEFAULT_LABELS = {
  it: {
    eyebrow: "Lezione gratuita completata",
    title: "Sblocca ora l'intera Academia VIP",
    subtitle_active: "Hai sbloccato un'offerta esclusiva: 30% di sconto sulla quota annuale, valida solo per 24 ore.",
    subtitle_expired: "L'offerta esclusiva è scaduta. Puoi comunque accedere all'Academia al prezzo pieno.",
    subtitle_inactive: "Hai completato la prima lezione gratuita. Continua il percorso completo dell'Academia VIP.",
    benefits_title: "Cosa include l'Academia VIP",
    b1: "Audio bilingue IT/ES per ogni lezione",
    b2: "Illustrazioni anatomiche premium HD",
    b3: "Quiz orientati all'esame ministeriale",
    b4: "Tutti i moduli del programma completo",
    b5: "Aggiornamenti futuri inclusi",
    progress_done: "Lezione 1 completata",
    countdown_label: "Offerta valida ancora",
    cta_active: "Approfitta del 30% di sconto",
    cta_inactive: "Accedi all'Academia VIP",
    price_was: "Prezzo pieno",
    price_now: "Solo per te",
    you_save: "Risparmi",
    close_aria: "Chiudi",
    h: "h", m: "m", s: "s",
  },
  es: {
    eyebrow: "Lección gratuita completada",
    title: "Desbloqueá toda la Academia VIP",
    subtitle_active: "Desbloqueaste una oferta exclusiva: 30% de descuento sobre la cuota anual, válida solo por 24 horas.",
    subtitle_expired: "La oferta exclusiva expiró. Igual podés acceder a la Academia al precio completo.",
    subtitle_inactive: "Completaste la primera lección gratuita. Continuá el recorrido completo de la Academia VIP.",
    benefits_title: "Qué incluye la Academia VIP",
    b1: "Audio bilingüe IT/ES en cada lección",
    b2: "Ilustraciones anatómicas premium HD",
    b3: "Cuestionarios orientados al examen ministerial",
    b4: "Todos los módulos del programa completo",
    b5: "Futuras actualizaciones incluidas",
    progress_done: "Lección 1 completada",
    countdown_label: "Oferta válida aún",
    cta_active: "Aprovechá el 30% de descuento",
    cta_inactive: "Acceder a la Academia VIP",
    price_was: "Precio completo",
    price_now: "Solo para vos",
    you_save: "Ahorrás",
    close_aria: "Cerrar",
    h: "h", m: "m", s: "s",
  },
};

/**
 * Trial-offer modal. Shown on:
 *   - Lesson 1 completion (reason="l1_complete")
 *   - Attempt to open a locked lesson (reason="locked_lesson")
 *
 * Display gating per session via sessionStorage key
 * `vip_trial_modal_shown_<reason>` so we never spam.
 *
 * Renders nothing for academy users (eligible=false).
 */
export function TrialOfferModal({ language = "it", trigger }) {
  const t = DEFAULT_LABELS[language] || DEFAULT_LABELS.it;
  const [offer, setOffer] = useState(null);
  const [open, setOpen] = useState(false);
  const lastTriggerRef = useRef(null);

  const fetchOffer = useCallback(async (start = false) => {
    try {
      const data = start
        ? await apiPost("/vip/trial-offer/start", {})
        : await apiGet("/vip/trial-offer");
      setOffer(data);
      return data;
    } catch (e) {
      // 401 = anon user, 403 = academy user → no offer for them
      return null;
    }
  }, []);

  // Trigger gating per session
  useEffect(() => {
    if (!trigger || !trigger.reason) return;
    if (trigger === lastTriggerRef.current) return; // de-dupe rapid re-renders
    lastTriggerRef.current = trigger;
    const key = `vip_trial_modal_shown_${trigger.reason}`;
    try {
      if (sessionStorage.getItem(key)) return;
    } catch { /* sessionStorage may be unavailable */ }
    (async () => {
      const startTimer = trigger.reason === "l1_complete";
      const data = await fetchOffer(startTimer);
      if (!data || !data.eligible) return; // academy user → silent
      // Even if expired or inactive, still prompt the user (locked-lesson trigger)
      setOpen(true);
      try { sessionStorage.setItem(key, "1"); } catch { /* noop */ }
      // Funnel event: modal actually rendered. Fire-and-forget; analytics
      // failures must never affect the user-facing UX.
      apiPost("/vip/funnel/event", {
        event_type: "trial_modal_shown",
        meta: { reason: trigger.reason, active: !!data.active },
      }).catch(() => {});
    })();
  }, [trigger, fetchOffer]);

  if (!offer || !offer.eligible) return null;

  return (
    <AnimatePresence>
      {open ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
                    style={{ background: "rgba(20, 12, 50, 0.78)", backdropFilter: "blur(8px)" }}
                    onClick={() => setOpen(false)}
                    data-testid="vip-trial-modal-backdrop">
          <motion.div initial={{ scale: 0.94, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
                      transition={{ type: "spring", damping: 22, stiffness: 280 }}
                      onClick={(e) => e.stopPropagation()}
                      data-testid="vip-trial-modal"
                      className="relative w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl"
                      style={{ background: "linear-gradient(145deg, #FFFFFF 0%, #FAF7FF 100%)" }}>
            <button onClick={() => setOpen(false)}
                    aria-label={t.close_aria}
                    data-testid="vip-trial-modal-close"
                    className="absolute top-4 right-4 z-10 h-9 w-9 rounded-full grid place-items-center transition-all hover:bg-black/10"
                    style={{ background: "rgba(0,0,0,0.04)" }}>
              <X className="h-5 w-5" style={{ color: "#5A3FC0" }} />
            </button>

            {/* Hero gradient */}
            <div className="px-7 sm:px-9 py-7 text-white"
                 style={{ background: "linear-gradient(135deg, #6B4DD8 0%, #4A2EAA 100%)" }}>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] font-medium mb-3"
                   style={{ background: "rgba(255,255,255,0.18)" }}>
                <Check className="h-3 w-3" /> {t.progress_done}
              </div>
              <p className="text-[10px] uppercase tracking-[0.2em] opacity-85 mb-1">{t.eyebrow}</p>
              <h2 className="font-serif text-2xl sm:text-3xl mb-2 leading-tight">{t.title}</h2>
              <p className="text-sm opacity-90 leading-relaxed">
                {offer.active ? t.subtitle_active : (offer.expired ? t.subtitle_expired : t.subtitle_inactive)}
              </p>
            </div>

            {/* Body */}
            <div className="px-7 sm:px-9 py-6">
              {offer.active ? <Countdown secondsLeft={offer.seconds_left} t={t} /> : null}

              <p className="text-[11px] uppercase tracking-[0.2em] mb-2.5 mt-1 font-medium" style={{ color: "#5A3FC0" }}>
                {t.benefits_title}
              </p>
              <ul className="space-y-2 mb-5">
                <Benefit icon={Headphones} label={t.b1} />
                <Benefit icon={ImageIcon} label={t.b2} />
                <Benefit icon={CircleHelp} label={t.b3} />
                <Benefit icon={Layers} label={t.b4} />
                <Benefit icon={Bell} label={t.b5} />
              </ul>

              {/* Price block */}
              <div className="rounded-2xl p-4 mb-5"
                   style={{ background: offer.active ? "linear-gradient(135deg, #FCE7EB 0%, #FFF1F4 100%)" : "#F3EFFE",
                            border: offer.active ? "1px solid #F4C5CD" : "1px solid #E5DEF7" }}>
                <div className="flex items-baseline justify-between gap-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] mb-0.5" style={{ color: "#7B5CE0" }}>{t.price_was}</p>
                    <p className="text-base line-through tabular-nums" style={{ color: "#9E97BA" }}>
                      €{offer.price_full_eur.toFixed(2)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-[0.2em] mb-0.5" style={{ color: "#5A3FC0" }}>{t.price_now}</p>
                    <p className="font-serif text-3xl tabular-nums" style={{ color: "#3D1F87" }}>
                      €{offer.price_final_eur.toFixed(2)}
                    </p>
                  </div>
                </div>
                {offer.active ? (
                  <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full"
                       style={{ background: "#9E2A48", color: "white" }}
                       data-testid="vip-trial-discount-pill">
                    <BadgePercent className="h-3 w-3" /> {t.you_save} €{(offer.price_full_eur - offer.price_final_eur).toFixed(2)} ({offer.discount_pct}%)
                  </div>
                ) : null}
              </div>

              <Link to={offer.payment_url || "/vip"}
                    onClick={(e) => {
                      // Funnel event: user clicked the CTA. Fire-and-forget so
                      // it never blocks the Stripe redirect.
                      apiPost("/vip/funnel/event", {
                        event_type: "trial_cta_clicked",
                        meta: { active: !!offer.active, discount_pct: offer.discount_pct },
                      }).catch(() => {});
                      // External Stripe link → standard navigation; keep React Router for /vip fallback.
                      if ((offer.payment_url || "").startsWith("http")) {
                        e.preventDefault();
                        window.location.href = offer.payment_url;
                      }
                    }}
                    target={(offer.payment_url || "").startsWith("http") ? undefined : "_self"}
                    data-testid="vip-trial-modal-cta"
                    className="flex items-center justify-center gap-2 w-full px-5 py-3.5 rounded-xl text-base font-medium text-white transition-all hover:scale-[1.02] hover:shadow-lg"
                    style={{ background: "linear-gradient(135deg, #6B4DD8 0%, #4A2EAA 100%)" }}>
                <Sparkles className="h-4 w-4" />
                {offer.active ? t.cta_active : t.cta_inactive}
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

function Benefit({ icon: Icon, label }) {
  return (
    <li className="flex items-start gap-2.5 text-sm" style={{ color: "#3D1F87" }}>
      <span className="shrink-0 h-6 w-6 rounded-full grid place-items-center mt-0.5"
            style={{ background: "#F3EFFE", color: "#5A3FC0" }}>
        <Icon className="h-3.5 w-3.5" strokeWidth={2} />
      </span>
      <span className="leading-snug">{label}</span>
    </li>
  );
}

function Countdown({ secondsLeft, t }) {
  const [secs, setSecs] = useState(secondsLeft || 0);
  useEffect(() => {
    if (secs <= 0) return undefined;
    const id = setInterval(() => setSecs((x) => Math.max(0, x - 1)), 1000);
    return () => clearInterval(id);
  }, [secs]);
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return (
    <div className="rounded-2xl p-4 mb-5 flex items-center gap-4"
         style={{ background: "linear-gradient(135deg, #2A1B5C 0%, #4A2EAA 100%)", color: "#FFFFFF" }}
         data-testid="vip-trial-countdown">
      <div className="shrink-0">
        <p className="text-[10px] uppercase tracking-[0.2em] opacity-80 mb-0.5">{t.countdown_label}</p>
      </div>
      <div className="flex items-center gap-2 flex-1 justify-end">
        <CountUnit n={h} unit={t.h} />
        <span className="font-serif text-2xl opacity-50 -mt-1">:</span>
        <CountUnit n={m} unit={t.m} />
        <span className="font-serif text-2xl opacity-50 -mt-1">:</span>
        <CountUnit n={s} unit={t.s} />
      </div>
    </div>
  );
}

function CountUnit({ n, unit }) {
  return (
    <div className="text-center">
      <div className="font-mono text-2xl sm:text-3xl tabular-nums leading-none">
        {String(n).padStart(2, "0")}
      </div>
      <div className="text-[9px] uppercase tracking-widest opacity-70 mt-0.5">{unit}</div>
    </div>
  );
}
